"use client";

import * as X from "@/ui";
import { Suspense, useEffect, useState } from "react";
import { useRoute } from "./common";

import { useSearchParams } from "next/navigation";
import { useIntercom } from "@/providers/intercom";

import { MenuMobile } from "./menu-mobile";
import { MenuDesktop } from "./menu-desktop";
import { FooterMobile } from "./footer-mobile";
import { FooterDesktop } from "./footer-desktop";

import WeChatQr from "@/assets/shared-qr-wechat.svg";
import { Footer, useFooter } from "@/providers/strapi/content/types/singleTypes/footer";
import ReactMarkdown from "react-markdown";
import { Banner, useBanner } from "@/providers/strapi/content/types/singleTypes/banner";

export function MainLayout({ children, withHeroImage, noTopPadding, ...pageProps }: X.PageProps<{
  children?: React.ReactNode;
  withHeroImage?: boolean;
  noTopPadding?: boolean;
}>) {

  const banner: Banner[] = useBanner()
  const bannerRef = X.useRef<HTMLDivElement>(null);
  const mainRef = X.useRef<HTMLDivElement>(null);
  const [bodyMarginTop, setBodyMarginTop] = useState(0)
  const isScrolled = useScrolledState({ passiveMode: true, minScroll: bodyMarginTop });
  const [mobileMenuOpen, setMenuMobileOpen] = X.useState(false);
  const [wechatQrOpen, setWechatQrOpen] = X.useState(false);

  const withTopPadding = !noTopPadding && !withHeroImage;

  X.useBodyScrollLock(mobileMenuOpen);

  const route = useRoute('Mainlayout');
  const showIntercom = route[0] !== "retail";
  useIntercom(showIntercom);

  useEffect(() => {
    if (bannerRef && bannerRef.current && mainRef && mainRef.current) {
      if (banner.length === 0) {
        setBodyMarginTop(0);
        return
      }
      // console.log("main top", mainRef.current.getBoundingClientRect().top);
      // console.log("banner bottom", bannerRef.current.getBoundingClientRect().bottom);
      const m = mainRef.current.getBoundingClientRect().top - bannerRef.current.getBoundingClientRect().bottom
      // console.log("m", (m * -1))
      setBodyMarginTop(m * -1)
    }
  }, [bannerRef, mainRef])


  return (
    <>

      <div className={X.clsx(
        "flex min-h-svh flex-col",
      )}>
        <div ref={bannerRef} className="w-full bg-black flex flex-col items-center  ">
          {banner && banner.map((b: Banner, index: number) => (
            <div key={`bannner-${index}`} className="flex flex-row gap-4 text-white px-6 max-w-content items-center py-5">
              <X.Image src={b.icon.url as string} width={b.icon.width} height={b.icon.height} alt="banner icon" className="w-6" />
              <ReactMarkdown
                components={{
                  // strong: ({ ...props }) => <strong {...props} className="text-yellow" />,
                  a: ({ ...props }) => <a {...props} target="_blank" className="underline" />,
                  p: ({ ...props }) => <p {...props} className="font-[400] [&_strong]:font-semibold [&_strong]:text-yellow" />
                }}>
                {b.message}
                {/* <p>
              <strong className="text-yellow">Délais de remboursement plus longs</strong> - Nos délais peuvent s’allonger pendant la période estivale (de juin à septembre). <a className=" cursor-pointer underline">En savoir plus</a>
            </p> */}
              </ReactMarkdown>
            </div>
          ))}

        </div>
        <MenuMobile
          {...pageProps}
          isOpen={mobileMenuOpen}
          setIsOpenAction={setMenuMobileOpen}
          withHeroImage={withHeroImage}
          isScrolled={isScrolled}
        />

        <MenuDesktop
          {...pageProps}
          withHeroImage={withHeroImage}
          isScrolled={isScrolled}
        />

        <main

          ref={mainRef} className={X.clsx(
            "flex-grow transition-opacity duration-300",
            mobileMenuOpen && "opacity-0" || "opacity-100",
            withTopPadding && "pt-10 ",
            // `-mt-[${bodyMarginTop}px]`
          )}

        // style={{
        //   marginTop: bodyMarginTop
        // }}
        >
          {children}
        </main>

        <div id="footer">
          <FooterDesktop
            {...pageProps}
            onShowWechatQrAction={() => setWechatQrOpen(true)}
          />

          <FooterMobile
            {...pageProps}
            className={X.clsx(
              "transition-opacity duration-300",
              mobileMenuOpen && "opacity-0" || "opacity-100",
            )}
          />
        </div>

        <Suspense>
          <WithAutoScroll />
        </Suspense>
      </div>

      <WeChatModal
        isOpen={wechatQrOpen}
        onClose={() => setWechatQrOpen(false)}
      />
    </>
  );
}


function WeChatModal(props: {
  isOpen: boolean;
  onClose: () => void;
}) {
  // const tr = X.useTranslator();
  const wechatModalMessage = useFooter('wechatModalMessage') as Footer['wechatModalMessage']
  return (
    <X.Modal onClose={props.onClose}>
      {props.isOpen && (
        <div className="cursor-pointer" onClick={() => props.onClose()}>
          <X.Image
            src={WeChatQr}
            alt="WeChat"
            className="rounded-2xl max-w-[80vw]"
          />
          <div className="bg-black rounded-b-2xl text-white flex flex-col items-center py-4 px-4 gap-2">
            {/* <p>{tr({ en: "Click anywhere to close", fr: "Cliquez n'importe où pour fermer", zh: "单击任意位置即可关闭" })}</p> */}
            <p>{wechatModalMessage}</p>
            <p className="text-yellow">单击任意位置即可关闭</p>
          </div>
        </div>
      )}
    </X.Modal>
  );
}


function useScrolledState(config?: {
  minScroll?: number;
  passiveMode?: boolean;
}) {
  const minScroll = config?.minScroll ?? 1;
  const [isScrolled, setIsScrolled] = X.useState(false);

  X.useEffect([isScrolled, minScroll], () => {
    const handleScroll = () => {
      if (document.body.style.position === "fixed") {
        // ignore when a modal is open
        return;
      }

      const shouldBeScrolled = window.scrollY > minScroll;

      if (shouldBeScrolled !== isScrolled) {
        setIsScrolled(shouldBeScrolled);
        // console.log("scroll", shouldBeScrolled)
      }
    };

    // passive listener -> better performance
    window.addEventListener("scroll", handleScroll, { passive: config?.passiveMode ?? false });

    // apply directly
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  });

  return isScrolled;
}


function WithAutoScroll() {
  useAutoScroll();

  return (<></>);
}


/**
 * Scroll automatically to the desired hash on page change/load
 */
export function useAutoScroll() {
  const pathname = X.usePathname();
  const searchParams = useSearchParams();

  X.useEffect([pathname, searchParams], () => {
    const hash = window.location.hash.slice(1);

    if (!hash) {
      return;
    }

    // try immediately (for static content)
    scrollTo(hash);

    // fallback for dynamically loaded content
    const observer = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) {
        scrollTo(hash);
        observer.disconnect();
      }
    }, {
      threshold: 0.1,
    });

    document.querySelectorAll("[id]").forEach(
      el => observer.observe(el)
    );

    // cleanup if not found
    const timeout = setTimeout(() => observer.disconnect(), 3000);

    return () => {
      observer.disconnect()
      clearTimeout(timeout)
    };

  });
}


function scrollTo(elementId: string) {
  const element = document.getElementById(elementId);

  if (element) {
    element.scrollIntoView({
      behavior: "smooth",
    });

    // remove the hash
    window.history.replaceState(null, "", window.location.pathname);
  }
}
